import "./app-pool.js";
import { z } from "zod";
//#region src/app-pool-tier-status.ts
/**
* The status of the `PoolTiers` objects of an app pool (2026-10-24-pool-machine-generations D-9,
* D-17, D-40, R-009, R-019): the four vocabularies and the two schemas the operator's tier
* controller writes, one entry per declared tier of one org's pool.
*
* Why this file is in `@zion/sdk-contracts` (R-019). Two shapes carry a `PoolTiers` status:
*
* 1. `AppPoolReportedStatusSchema` in `@zion/sdk-cp-dp-contracts`, the pool row of the DP status
*    report, as its optional member `poolTiers`.
* 2. `appPoolAdminRowSchema` in `./app-pool-admin.ts`, the row site-admin-api serves to the
*    console, as its nullable member `poolTiers`.
*
* `@zion/sdk-contracts` cannot import `@zion/sdk-cp-dp-contracts` (a build cycle), so the one
* declaration lives here, and `@zion/sdk-cp-dp-contracts` re-exports it under the same names.
* The list schema of several objects and its cap (`AppPoolTiersStatusListSchema`,
* `APP_POOL_TIERS_STATUS_MAX`) stay in `@zion/sdk-cp-dp-contracts`: the cap is its
* `APP_POOL_NODE_GROUP_MAX`, and no shape of this package reads the list.
*
* Every export is pure and frozen or immutable. Nothing here performs I/O.
*/
/**
* The states of one tier of a `PoolTiers` object (2026-10-24-pool-machine-generations D-9, D-24,
* D-40, D-17), in the order a tier usually passes through them:
*
* - `open`: new attempt pods of the pool admit the tier's node group (D-22).
* - `closed`: the tier is kept and its node group exists, and new pods do not admit it. A later
*   tier waits closed until every earlier tier is at its cap or in backoff (D-40), and the last open
*   tier closes first when demand drops (D-24).
* - `backoff`: the tier failed (D-40), and new pods do not admit it until `backoffUntil`.
* - `dropped`: the operator keeps no node group for the tier on this data plane. `dropReason` says
*   why (D-15, D-17, D-44).
*
* Frozen. The operator's tier controller (WS-2b) writes these values; the `PoolTiers` CRD status
* (WS-2c), the reported pool row, the admin row and the console (WS-2g) read them.
*/
const APP_POOL_TIER_STATES = Object.freeze([
	"open",
	"closed",
	"backoff",
	"dropped"
]);
/**
* Why the operator drops a declared tier on a data plane (D-15, D-17, D-44):
*
* - `other-cloud`: the tier names a cloud other than the data plane's cloud.
* - `not-enabled`: the data plane enables no machine type of the tier's family, or no machine type
*   of the tier's accelerator shape (`dpEnabledMachineTypes`).
* - `no-fit`: the data plane enables machine types of the tier, and none of them holds the pool's
*   node vector.
* - `accelerator-unavailable`: the accelerator table has no entry for the tier's model on the data
*   plane's cloud (D-44). Publish refuses such a tier, so only an older table on the operator side
*   gives it.
*
* Frozen. A pool whose every tier is dropped reads `unschedulable` with `no-tier-for-cloud` (D-17).
*/
const APP_POOL_TIER_DROP_REASONS = Object.freeze([
	"other-cloud",
	"not-enabled",
	"no-fit",
	"accelerator-unavailable"
]);
/**
* The refusals the tier controller charges to a tier (D-3, D-40). Each one sends the tier to
* `backoff`:
*
* - `scale-up-refused`: the cluster autoscaler refused to add a node for a Pending attempt pod
*   (`FailedScaleUp`, `NotTriggerScaleUp`). Its text names no group, so the controller charges it to
*   the newest open tier (D-40).
* - `pending-past-limit`: every earlier tier is at its cap, and attempt pods stayed Pending past the
*   operator's time limit (10 minutes by default, D-40).
* - `no-ready-nodes`: the tier kept 0 Ready nodes past the same time limit (D-40).
*
* Frozen.
*/
const APP_POOL_TIER_REFUSALS = Object.freeze([
	"scale-up-refused",
	"pending-past-limit",
	"no-ready-nodes"
]);
/**
* What the operator did with the tier cap overrides of one `PoolTiers` object in its last cycle
* (D-11, R-009), from the envelope member `appPoolTierCaps`:
*
* - `none`: the envelope holds no entry for the object's org, app and pool, or the pool is in-mesh,
*   which takes no entry (R-009).
* - `applied`: each entry replaced its tier's cap.
* - `held`: the merged caps of the pool on the data plane's cloud total more than 100, so the
*   operator applied no entry of the pool in that cycle, and every tier runs at its declared cap
*   (R-009).
*
* Frozen.
*/
const APP_POOL_TIER_CAP_OVERRIDE_STATES = Object.freeze([
	"none",
	"applied",
	"held"
]);
/** The longest `groupId` a tier status carries: `apg-` plus 20 hex characters. */
const APP_POOL_TIER_STATUS_GROUP_ID_MAX_LENGTH = 24;
/** The longest `orgId` a tier set status carries: a 36-character UUID. */
const APP_POOL_TIER_STATUS_ORG_ID_MAX_LENGTH = 36;
/** The grammar of a tier set's `orgId`: a lowercase 8-4-4-4-12 hex UUID, the form the CP gives an org. */
const APP_POOL_TIER_STATUS_ORG_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/**
* The status of one declared tier of one `PoolTiers` object (2026-10-24-pool-machine-generations
* D-9, section 3.2 of `overview.md`): the tier, its state, its node group, its cap in force, its
* Ready nodes, its backoff end and its last refusal.
*
* Producer: the operator's tier controller (WS-2b), one entry per declared tier of the pool, in the
* declared order, the dropped tiers included. Readers: the `PoolTiers` CRD status (WS-2c) and the
* console's tier view (WS-2g). Members:
*
* - `tier`: the tier key, the output of `appPoolTierKey` (`gcp.c4d.spot`), 1 to `APP_POOL_TIER_KEY_MAX_LENGTH` (34) characters.
* - `state`: one of {@link APP_POOL_TIER_STATES}.
* - `groupId`: the tier's node group, the output of `appPoolNodeGroupId` with the tier key, or
*   `null` for a `dropped` tier. 1 to 24 characters.
* - `maxNodes`: the tier's cap in force: the override when the operator applied one, else the
*   declared cap, after the operator's `maxNodesPerPool` clamp (D-25). A dropped tier carries its
*   declared cap. A whole number from 0.
* - `readyNodes`: the Ready nodes with the tier's group label, at report time. A whole number from 0.
* - `backoffUntil`: the instant the tier leaves `backoff`, an ISO 8601 UTC instant
*   (`toISOString()`), or `null` when the tier is in no backoff.
* - `lastRefusal`: the last refusal the controller charged to the tier, one of
*   {@link APP_POOL_TIER_REFUSALS}, or `null` when it charged none since the operator started.
* - `dropReason`: one of {@link APP_POOL_TIER_DROP_REASONS} on a `dropped` tier, else `null`.
*
* The producer holds these pairs: `groupId` and `dropReason` are `null` and non-null exactly
* opposite to each other and to `state === 'dropped'`; `backoffUntil` is non-null exactly on
* `backoff`; `lastRefusal` is non-null on `backoff`. No `.refine` ties them. This is the posture of
* `AppPoolReportedStatusSchema`: the shape is a measurement, and a refusal of one mismatched
* pair would take a whole report down to hide one value. The counts carry no upper bound for the
* same reason: a post-clamp value above its cap is a controller defect, and the clamp is enforced
* where the clamp happens.
*
* `.strict()`. A conforming object parses to itself; an unknown key, a value outside an enum, a
* negative or fractional count, an empty or over-long string, or a `backoffUntil` that is not a UTC
* instant is a `ZodError`. Pure.
*/
const AppPoolTierStatusSchema = z.object({
	tier: z.string().min(1).max(34),
	state: z.enum(APP_POOL_TIER_STATES),
	groupId: z.string().min(1).max(APP_POOL_TIER_STATUS_GROUP_ID_MAX_LENGTH).nullable(),
	maxNodes: z.number().int().nonnegative(),
	readyNodes: z.number().int().nonnegative(),
	backoffUntil: z.string().datetime().nullable(),
	lastRefusal: z.enum(APP_POOL_TIER_REFUSALS).nullable(),
	dropReason: z.enum(APP_POOL_TIER_DROP_REASONS).nullable()
}).strict();
/**
* The status of one `PoolTiers` object (D-9): one org's tiers of one pool of one app on the data
* plane. The pool's app and name ride beside it (the `PoolTiers` spec, or the pool row that carries
* it).
*
* - `orgId`: the org whose node groups the tiers name, a lowercase UUID ({@link APP_POOL_TIER_STATUS_ORG_ID_PATTERN}),
*   or `null` for an in-mesh pool, whose groups carry no org.
* - `capOverrides`: one of {@link APP_POOL_TIER_CAP_OVERRIDE_STATES}.
* - `tiers`: one {@link AppPoolTierStatusSchema} per declared tier, in the declared order, 1 to
*   `APP_POOL_MAX_TIERS` (8). For a pool with `class`, the tiers the operator expanded from the
*   class ladder (D-16).
*
* Readers (R-019): the `PoolTiers` CRD status (WS-2c), `AppPoolReportedStatusSchema.poolTiers` of
* `@zion/sdk-cp-dp-contracts` (optional) and `appPoolAdminRowSchema.poolTiers` (nullable).
*
* The producer writes each tier key once. No `.refine` checks it, for the reason
* {@link AppPoolTierStatusSchema} gives. `.strict()`; a conforming object parses to itself. Pure.
*/
const AppPoolTiersStatusSchema = z.object({
	orgId: z.string().max(APP_POOL_TIER_STATUS_ORG_ID_MAX_LENGTH).regex(APP_POOL_TIER_STATUS_ORG_ID_PATTERN).nullable(),
	capOverrides: z.enum(APP_POOL_TIER_CAP_OVERRIDE_STATES),
	tiers: z.array(AppPoolTierStatusSchema).min(1).max(8)
}).strict();
//#endregion
export { APP_POOL_TIER_CAP_OVERRIDE_STATES, APP_POOL_TIER_DROP_REASONS, APP_POOL_TIER_REFUSALS, APP_POOL_TIER_STATES, AppPoolTierStatusSchema, AppPoolTiersStatusSchema };

//# sourceMappingURL=app-pool-tier-status.js.map